import {useLazyLoadQuery, type FetchPolicy, graphql} from 'react-relay'
import {useCallback, useEffect, useState} from 'react'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {NestedListView} from '@github-ui/nested-list-view'
import {NestedListViewHeader} from '@github-ui/nested-list-view/NestedListViewHeader'
import {NestedListViewHeaderTitle} from '@github-ui/nested-list-view/NestedListViewHeaderTitle'
import {NestedListViewCompletionPill} from '@github-ui/nested-list-view/NestedListViewCompletionPill'

import {SubIssuesListItem} from './SubIssuesListItem'
import type {SubIssuesListViewQuery} from './__generated__/SubIssuesListViewQuery.graphql'

const SubIssuesListViewGraphQLQuery = graphql`
  query SubIssuesListViewQuery($issueID: ID!) {
    node(id: $issueID) {
      ... on Issue {
        id
        subIssues(first: 50) {
          nodes {
            id
            ...SubIssuesListItem
          }
        }
        subIssuesSummary {
          total
          completed
          percentCompleted
        }
      }
    }
  }
`

export function SubIssuesListView({
  issueID,
  shouldRefetch,
  onRefetch,
  onSubIssueClick,
}: {
  issueID: string
  /** Indicates whether the sub-issues list data should be refetched (e.g., if it is stale) */
  shouldRefetch: boolean
  /** Callback called when we've sent the request to refetch the sub-issues data */
  onRefetch: () => void
  /** Callback called when a sub-issue is clicked */
  onSubIssueClick?: (itemIdentifier: ItemIdentifier, url: string) => void
}) {
  // This looks a little bit unconvential, but this approach was taken from the Relay docs here:
  // https://relay.dev/docs/guided-tour/refetching/refreshing-queries/#when-using-uselazyloadquery
  // In the future, we may be able to replace this refetching with a refetchable fragment that exists
  // in one of the issue-viewer queries, rather than making a separate query and needing to refetch manually.
  const [fetchOptions, setFetchOptions] = useState<{fetchKey: number; fetchPolicy: FetchPolicy} | null>(null)

  const refetch = useCallback(() => {
    setFetchOptions(prevOptions => ({
      fetchKey: (prevOptions?.fetchKey ?? 0) + 1,
      fetchPolicy: 'store-and-network',
    }))
    onRefetch()
  }, [onRefetch])

  useEffect(() => {
    // Refetch the sub-issues data when indicated (e.g., data is stale because a sub-issue was added or removed)
    if (shouldRefetch) {
      refetch()
    }
  }, [refetch, shouldRefetch])

  const data = useLazyLoadQuery<SubIssuesListViewQuery>(SubIssuesListViewGraphQLQuery, {issueID}, fetchOptions ?? {})

  if (!data.node?.subIssues) return null
  if (!data.node?.subIssuesSummary) return null

  const {total, completed, percentCompleted} = data.node.subIssuesSummary

  return (
    <NestedListView
      title="Sub-issues"
      header={
        <NestedListViewHeader
          title={<NestedListViewHeaderTitle title="Sub issues" />}
          completionPill={
            <NestedListViewCompletionPill
              progress={{
                total,
                completed,
                percentCompleted,
              }}
            />
          }
          sx={{background: 'none', height: 40, borderBottom: 0}}
        />
      }
      isCollapsible={true}
    >
      {data.node.subIssues.nodes?.map(issueNode => {
        if (!issueNode) return null
        if (!data.node?.id) return null
        return (
          <SubIssuesListItem
            key={issueNode.id}
            issueKey={issueNode}
            parentIssueId={data.node.id}
            onSubIssueClick={onSubIssueClick}
          />
        )
      })}
    </NestedListView>
  )
}

try{ SubIssuesListView.displayName ||= 'SubIssuesListView' } catch {}